import {
  SystemSnapshot,
  SystemSnapshotMetadata,
  SystemSnapshotSummary,
  OfficeLocation,
  SimulationStats,
  TimeMachineIncident,
  TimeMachineFrame,
  SystemTimezone,
} from '../types';

const SNAPSHOTS_STORAGE_KEY = 'isometric_office_system_snapshots_v1';

/**
 * Builds a complete SystemSnapshot data structure capturing all system state.
 */
export function buildCurrentSnapshot(params: {
  locations: OfficeLocation[];
  activeLocationId: string;
  mode: 'simulation' | 'editor';
  activeTimezoneIana: string;
  clockTime: string;
  simulationSpeed: number;
  isSimulationPlaying: boolean;
  simulationStats?: SimulationStats;
  incidents: TimeMachineIncident[];
  timeMachineFrames: TimeMachineFrame[];
  currentTimeMachineFrameIndex: number;
  isTimeMachineLive: boolean;
  systemTimezones: SystemTimezone[];
  userProfile?: any;
  customName?: string;
  customDescription?: string;
  tag?: 'manual' | 'incident' | 'checkpoint' | 'imported' | 'auto';
}): SystemSnapshot {
  const activeLoc =
    params.locations.find((l) => l.id === params.activeLocationId) ||
    params.locations[0];

  const now = new Date();
  const id = `snapshot_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const defaultName =
    params.customName?.trim() ||
    `Snapshot ${activeLoc ? activeLoc.name : 'System'} (${params.clockTime})`;

  // Calculate summary metrics across all locations
  let totalRooms = 0;
  let totalAgents = 0;
  let totalFurniture = 0;
  let activeAgentsCount = 0;
  let idleAgentsCount = 0;
  let breakAgentsCount = 0;

  params.locations.forEach((loc) => {
    totalRooms += loc.rooms?.length || 0;
    totalFurniture += loc.items?.length || 0;
    if (loc.agents) {
      totalAgents += loc.agents.length;
      loc.agents.forEach((ag) => {
        if (ag.status === 'working' || ag.status === 'meeting' || ag.status === 'walking') {
          activeAgentsCount++;
        } else if (ag.status === 'coffee_break' || ag.status === 'bathroom_break') {
          breakAgentsCount++;
        } else {
          idleAgentsCount++;
        }
      });
    }
  });

  const defaultStats: SimulationStats = {
    totalTasksCompleted: 124,
    tasksInQueue: 18,
    throughputTasksPerMin: 14.5,
    avgSlaLatencySec: 4.8,
    officeEfficiencyPct: 92,
    activeBottlenecks: [],
    history: [],
  };

  const currentStats = params.simulationStats || defaultStats;

  const summary: SystemSnapshotSummary = {
    totalLocations: params.locations.length,
    totalRooms,
    totalAgents,
    totalFurniture,
    activeAgentsCount,
    idleAgentsCount,
    breakAgentsCount,
    avgEfficiency: currentStats.officeEfficiencyPct,
    throughputTasksPerMin: currentStats.throughputTasksPerMin,
    activeBottlenecksCount: currentStats.activeBottlenecks?.length || 0,
  };

  const metadata: SystemSnapshotMetadata = {
    id,
    name: defaultName,
    description: params.customDescription || '',
    createdAt: now.toISOString(),
    clockTime: params.clockTime,
    version: '1.0.0',
    mode: params.mode,
    activeLocationId: params.activeLocationId,
    activeLocationName: activeLoc?.name || 'Main Floor',
    activeTimezoneIana: params.activeTimezoneIana,
    tag: params.tag || 'manual',
  };

  return {
    metadata,
    locations: JSON.parse(JSON.stringify(params.locations)),
    simulation: {
      speed: params.simulationSpeed,
      isPlaying: params.isSimulationPlaying,
      stats: JSON.parse(JSON.stringify(currentStats)),
      incidents: JSON.parse(JSON.stringify(params.incidents)),
      timeMachineFramesCount: params.timeMachineFrames.length,
      timeMachineFrames: JSON.parse(JSON.stringify(params.timeMachineFrames.slice(-30))), // store recent historical frames
      currentTimeMachineFrameIndex: params.currentTimeMachineFrameIndex,
      isTimeMachineLive: params.isTimeMachineLive,
    },
    systemTimezones: JSON.parse(JSON.stringify(params.systemTimezones)),
    userProfile: params.userProfile ? JSON.parse(JSON.stringify(params.userProfile)) : undefined,
    summary,
  };
}

/**
 * Validates a JSON string or object to ensure it conforms to the SystemSnapshot schema.
 */
export function validateSnapshotJSON(input: string | any): {
  isValid: boolean;
  errors: string[];
  snapshot?: SystemSnapshot;
} {
  const errors: string[] = [];
  let parsed: any;

  if (typeof input === 'string') {
    try {
      parsed = JSON.parse(input);
    } catch (e) {
      return { isValid: false, errors: [`Invalid JSON: ${e.message}`] };
    }
  } else {
    parsed = input;
  }

  if (!parsed || typeof parsed !== 'object') {
    return { isValid: false, errors: ['The structure must be a valid JSON object.'] };
  }

  // Validate locations
  if (!parsed.locations || !Array.isArray(parsed.locations) || parsed.locations.length === 0) {
    errors.push('The "locations" array is missing or empty.');
  } else {
    const invalidLoc = parsed.locations.find((l: any) => !l.id || typeof l.gridWidth !== 'number');
    if (invalidLoc) {
      errors.push('One or more locations lack a valid ID or dimensions.');
    }
  }

  // Validate or repair metadata
  if (!parsed.metadata || typeof parsed.metadata !== 'object') {
    parsed.metadata = {
      id: `snapshot_${Date.now()}`,
      name: 'Imported Snapshot',
      createdAt: new Date().toISOString(),
      clockTime: '12:00:00',
      version: '1.0.0',
      mode: 'simulation',
      activeLocationId: parsed.locations?.[0]?.id || 'loc_1',
      activeTimezoneIana: 'Europe/Madrid',
      tag: 'imported',
    };
  }

  // Validate simulation state
  if (!parsed.simulation || typeof parsed.simulation !== 'object') {
    parsed.simulation = {
      speed: 1,
      isPlaying: true,
      stats: {
        totalTasksCompleted: 100,
        tasksInQueue: 10,
        throughputTasksPerMin: 12,
        avgSlaLatencySec: 5,
        officeEfficiencyPct: 90,
        activeBottlenecks: [],
        history: [],
      },
      incidents: [],
      timeMachineFramesCount: 0,
      timeMachineFrames: [],
      currentTimeMachineFrameIndex: 0,
      isTimeMachineLive: true,
    };
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  return { isValid: true, errors: [], snapshot: parsed as SystemSnapshot };
}

/**
 * Downloads a SystemSnapshot as a formatted .json file in the user's browser.
 */
export function downloadSnapshotAsJSON(snapshot: SystemSnapshot, customFileName?: string) {
  const dateStr = snapshot.metadata.createdAt.replace(/[:.]/g, '-').slice(0, 19);
  const cleanName = (snapshot.metadata.name || 'snapshot')
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, '_');
  const filename = customFileName || `${cleanName}_${dateStr}.json`;

  const jsonStr = JSON.stringify(snapshot, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Copies formatted snapshot JSON to clipboard.
 */
export async function copySnapshotToClipboard(snapshotOrSection: any): Promise<boolean> {
  try {
    const jsonStr = JSON.stringify(snapshotOrSection, null, 2);
    await navigator.clipboard.writeText(jsonStr);
    return true;
  } catch (err) {
    console.error('Error copying JSON to clipboard:', err);
    return false;
  }
}

/**
 * Persistent LocalStorage Snapshot Library
 */
export function loadSavedSnapshots(): SystemSnapshot[] {
  try {
    const raw = localStorage.getItem(SNAPSHOTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error loading snapshots from localStorage:', e);
    return [];
  }
}

export function saveSnapshotToLibrary(snapshot: SystemSnapshot): SystemSnapshot[] {
  try {
    const existing = loadSavedSnapshots();
    const filtered = existing.filter((s) => s.metadata.id !== snapshot.metadata.id);
    const updated = [snapshot, ...filtered].slice(0, 30); // Keep up to 30 snapshots
    localStorage.setItem(SNAPSHOTS_STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error('Error saving snapshot to localStorage:', e);
    return [];
  }
}

export function deleteSnapshotFromLibrary(snapshotId: string): SystemSnapshot[] {
  try {
    const existing = loadSavedSnapshots();
    const updated = existing.filter((s) => s.metadata.id !== snapshotId);
    localStorage.setItem(SNAPSHOTS_STORAGE_KEY, JSON.stringify(updated));
    return updated;
  } catch (e) {
    console.error('Error deleting snapshot from localStorage:', e);
    return [];
  }
}
